import React, { useEffect } from 'react';
import { CardContent, Grid, Typography } from '@mui/material';
import type { PageConfig } from 'c-config';
import { PostAuthRoutes } from 'c-routes';
import { PermissionName } from 'c-sdk';
import { Translate, useCommonTranslation } from 'c-translation';
import { IconLinkButton, TransparentCard } from 'c-components';
import { DashboardTileSize } from 'c-dashboard-module/types';
import RoleListPage from 'c-admin/Pages/RoleListPage';
import SkinListPage from 'c-admin/Pages/SkinListPage';
import UserListPage from 'c-admin/Pages/UserListPage';
import MarketListPage from 'c-admin/Pages/EditMarketPage/MarketListPage';
import ImpersonateUserPage from 'c-admin/Pages/ImpersonateUserPage/ImpersonateUserPage';
import FrameSearchPage from 'c-admin/Pages/FrameSearchPage/FrameSearchPage';
import { IfHasAllPermissions } from 'c-auth-module/Components';
import { adminPanelIcon } from 'c-main/icons';
import DashboardTile from 'c-admin/Components/DashboardTile';
import { PopDisplaysAdminTiles } from 'c-pop-displays/Components';
import { DisplaysAdminTiles } from 'c-displays/Components';
import { PopDisplaysHomePage } from 'c-pop-displays/Pages';
import { DisplaysAdminHomePage } from 'c-displays/Pages';
import { ArrowForward } from '@mui/icons-material';
import ReactGA from 'react-ga';
import TaskQueuePage from 'c-admin/Pages/TaskQueuePage/TaskQueuePage';
import UsageOverviewPage from 'c-admin/Pages/Usage/UsageOverviewPage';
import DashboardSetupPage from 'c-admin/Pages/DashboardSetupPage/DashboardSetupPage';
import SummarySetupPage from 'c-admin/Pages/SummarySetupPage/SummarySetupPage';
import { AgencyListPage, BuyerListPage, DataStatusPage } from 'c-main/Pages';
import ClientListPage from 'c-main/Pages/Client/ClientListPage';
import DataStatusLineItemsPage from 'c-admin/Pages/DataStatusLineItems';
import NotificationsListPage from 'c-admin/Pages/NotificationsList';

const AdminHomePage = () => {
    useEffect(() => {
        ReactGA.pageview(window.location.pathname + window.location.search);
    }, []);
    return (
        <TransparentCard title={useCommonTranslation('Modules.Admin.Home.metaTitle')}>
            <CardContent>
                <Grid container gap={4}>
                    <IfHasAllPermissions
                        permissions={DataStatusPage.systemSearch.permissions}
                        disableDebugWrap
                    >
                        <DashboardTile
                            id="admin-data-status"
                            size={DashboardTileSize.Medium}
                            title={(<Translate path="Navigation.dataStatus" />) as any}
                            titleIcon={DataStatusPage.systemSearch.icon}
                            route={DataStatusPage.systemSearch.route}
                            permissions={DataStatusPage.systemSearch.permissions}
                        />
                    </IfHasAllPermissions>
                    <IfHasAllPermissions
                        permissions={DataStatusLineItemsPage.systemSearch.permissions}
                        disableDebugWrap
                    >
                        <DashboardTile
                            id="admin-data-status-Line-Items"
                            size={DashboardTileSize.Medium}
                            title={(<Translate path="Navigation.dataStatusLineItems" />) as any}
                            titleIcon={DataStatusLineItemsPage.systemSearch.icon}
                            route={DataStatusLineItemsPage.systemSearch.route}
                            permissions={DataStatusLineItemsPage.systemSearch.permissions}
                        />
                    </IfHasAllPermissions>
                    <IfHasAllPermissions permissions={[PermissionName.Admin]} disableDebugWrap>
                        <DashboardTile
                            id="admin-notifications"
                            size={DashboardTileSize.Medium}
                            title={(<Translate path="Navigation.adminNotifications" />) as any}
                            titleIcon={NotificationsListPage.systemSearch.icon}
                            route={NotificationsListPage.systemSearch.route}
                            permissions={NotificationsListPage.systemSearch.permissions}
                        />
                    </IfHasAllPermissions>
                    <IfHasAllPermissions
                        permissions={AgencyListPage.systemSearch.permissions}
                        disableDebugWrap
                    >
                        <DashboardTile
                            id="admin-agencys"
                            size={DashboardTileSize.Medium}
                            title={(<Translate path="Navigation.agencies" />) as any}
                            titleIcon={AgencyListPage.systemSearch.icon}
                            route={AgencyListPage.systemSearch.route}
                            permissions={AgencyListPage.systemSearch.permissions}
                        />
                    </IfHasAllPermissions>
                    <IfHasAllPermissions
                        permissions={BuyerListPage.systemSearch.permissions}
                        disableDebugWrap
                    >
                        <DashboardTile
                            id="admin-buyer-cost"
                            size={DashboardTileSize.Medium}
                            title={(<Translate path="Navigation.buyers" />) as any}
                            titleIcon={BuyerListPage.systemSearch.icon}
                            route={BuyerListPage.systemSearch.route}
                            permissions={BuyerListPage.systemSearch.permissions}
                        />
                    </IfHasAllPermissions>
                    <IfHasAllPermissions
                        permissions={ClientListPage.systemSearch.permissions}
                        disableDebugWrap
                    >
                        <DashboardTile
                            id="admin-clients"
                            size={DashboardTileSize.Medium}
                            title={(<Translate path="Navigation.clients" />) as any}
                            titleIcon={ClientListPage.systemSearch.icon}
                            route={ClientListPage.systemSearch.route}
                            permissions={ClientListPage.systemSearch.permissions}
                        />
                    </IfHasAllPermissions>
                    <IfHasAllPermissions
                        permissions={SkinListPage.systemSearch.permissions}
                        disableDebugWrap
                    >
                        <DashboardTile
                            id="admin-skins"
                            size={DashboardTileSize.Medium}
                            title={(<Translate path="Navigation.adminSkins" />) as any}
                            titleIcon={SkinListPage.systemSearch.icon}
                            route={SkinListPage.systemSearch.route}
                            permissions={SkinListPage.systemSearch.permissions}
                        />
                    </IfHasAllPermissions>
                    <IfHasAllPermissions
                        permissions={MarketListPage.systemSearch.permissions}
                        disableDebugWrap
                    >
                        <DashboardTile
                            id="admin-markets"
                            size={DashboardTileSize.Medium}
                            title={(<Translate path="Navigation.adminMarkets" />) as any}
                            titleIcon={MarketListPage.systemSearch.icon}
                            route={MarketListPage.systemSearch.route}
                            permissions={MarketListPage.systemSearch.permissions}
                        />
                    </IfHasAllPermissions>
                    <IfHasAllPermissions
                        permissions={UserListPage.systemSearch.permissions}
                        disableDebugWrap
                    >
                        <DashboardTile
                            id="admin-users"
                            size={DashboardTileSize.Medium}
                            title={(<Translate path="Navigation.adminUsers" />) as any}
                            titleIcon={UserListPage.systemSearch.icon}
                            route={UserListPage.systemSearch.route}
                            permissions={UserListPage.systemSearch.permissions}
                        />
                    </IfHasAllPermissions>
                    <IfHasAllPermissions
                        permissions={RoleListPage.systemSearch.permissions}
                        disableDebugWrap
                    >
                        <DashboardTile
                            id="admin-roles"
                            size={DashboardTileSize.Medium}
                            title={(<Translate path="Navigation.adminRoles" />) as any}
                            titleIcon={RoleListPage.systemSearch.icon}
                            route={RoleListPage.systemSearch.route}
                            permissions={RoleListPage.systemSearch.permissions}
                        />
                    </IfHasAllPermissions>
                    <IfHasAllPermissions
                        permissions={ImpersonateUserPage.systemSearch.permissions}
                        disableDebugWrap
                    >
                        <DashboardTile
                            id="admin-impersonate"
                            size={DashboardTileSize.Medium}
                            title={(<Translate path="Navigation.adminImpersonateUser" />) as any}
                            titleIcon={ImpersonateUserPage.systemSearch.icon}
                            route={ImpersonateUserPage.systemSearch.route}
                            permissions={ImpersonateUserPage.systemSearch.permissions}
                        />
                    </IfHasAllPermissions>
                    <IfHasAllPermissions
                        permissions={FrameSearchPage.systemSearch.permissions}
                        disableDebugWrap
                    >
                        <DashboardTile
                            id="admin-frame-search"
                            size={DashboardTileSize.Medium}
                            title={(<Translate path="Navigation.adminFrameSearch" />) as any}
                            titleIcon={FrameSearchPage.systemSearch.icon}
                            route={FrameSearchPage.systemSearch.route}
                            permissions={FrameSearchPage.systemSearch.permissions}
                        />
                    </IfHasAllPermissions>
                    <IfHasAllPermissions
                        permissions={UsageOverviewPage.systemSearch.permissions}
                        disableDebugWrap
                    >
                        <DashboardTile
                            id="admin-usage"
                            size={DashboardTileSize.Medium}
                            title={(<Translate path="Navigation.adminUsage" />) as any}
                            titleIcon={UsageOverviewPage.systemSearch.icon}
                            route={UsageOverviewPage.systemSearch.route}
                            permissions={UsageOverviewPage.systemSearch.permissions}
                        />
                    </IfHasAllPermissions>
                    <IfHasAllPermissions
                        permissions={TaskQueuePage.systemSearch.permissions}
                        disableDebugWrap
                    >
                        <DashboardTile
                            id="admin-pop-task-queue"
                            size={DashboardTileSize.Medium}
                            title={(<Translate path="Navigation.popDisplaysTaskQueue" />) as any}
                            titleIcon={TaskQueuePage.systemSearch.icon}
                            route={TaskQueuePage.systemSearch.route}
                            permissions={TaskQueuePage.systemSearch.permissions}
                        />
                    </IfHasAllPermissions>
                    <IfHasAllPermissions
                        permissions={DashboardSetupPage.systemSearch.permissions}
                        disableDebugWrap
                    >
                        <DashboardTile
                            id="admin-dashboard-setup"
                            size={DashboardTileSize.Medium}
                            title={(<Translate path="Navigation.adminDashboardSetup" />) as any}
                            titleIcon={DashboardSetupPage.systemSearch.icon}
                            route={DashboardSetupPage.systemSearch.route}
                            permissions={DashboardSetupPage.systemSearch.permissions}
                        />
                    </IfHasAllPermissions>
                    <IfHasAllPermissions
                        permissions={SummarySetupPage.systemSearch.permissions}
                        disableDebugWrap
                    >
                        <DashboardTile
                            id="admin-summary-setup"
                            size={DashboardTileSize.Medium}
                            title={(<Translate path="Navigation.adminSummarySetup" />) as any}
                            titleIcon={SummarySetupPage.systemSearch.icon}
                            route={SummarySetupPage.systemSearch.route}
                            permissions={SummarySetupPage.systemSearch.permissions}
                        />
                    </IfHasAllPermissions>
                </Grid>
                <Typography variant="h3" color="textSecondary" my={2}>
                    {useCommonTranslation('Modules.PopDisplays.adminHomeSectionTitle')}
                    <IconLinkButton to={PopDisplaysHomePage.systemSearch.route}>
                        <ArrowForward fontSize="inherit" color="primary" />
                    </IconLinkButton>
                </Typography>
                <PopDisplaysAdminTiles />
                <Typography variant="h3" color="textSecondary" my={2}>
                    {useCommonTranslation('Modules.Displays.adminHomeSectionTitle')}
                    <IconLinkButton to={DisplaysAdminHomePage.systemSearch.route}>
                        <ArrowForward fontSize="inherit" color="primary" />
                    </IconLinkButton>
                </Typography>
                <DisplaysAdminTiles />
            </CardContent>
        </TransparentCard>
    );
};

const setup: PageConfig = {
    id: 'AdminHomePage',
    component: AdminHomePage,
    systemSearch: {
        title: 'Modules.Admin.Home.metaTitle',
        description: 'Modules.Admin.Home.description',
        icon: adminPanelIcon,
        type: 'page',
        aliases: 'SystemSearch.aliases.adminHomePage',
        route: PostAuthRoutes.Admin.AdminHome,
        saveAsRecentPage: true,
        breadcrumbs: [],
        permissions: [PermissionName.Admin],
    },
};

export default setup;
